import { Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { CONTENT_LABELS, CONTENT_ORDER, type BgtHistorySummary } from '@/lib/api/bgt-history';

interface Props {
  title: string;
  summary: BgtHistorySummary;
  from: string;
  to: string;
}

function formatDuration(ms: number): string {
  if (ms <= 0) return '0분';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}분`;
  const h = Math.floor(min / 60);
  const remM = min % 60;
  return remM > 0 ? `${h}시간 ${remM}분` : `${h}시간`;
}

/**
 * BgtHistory 응답의 summary 카드 — 탭2(기간 선택)와 탭5(최근 4주 요약)에서 공용.
 */
export function HistorySummaryCard({ title, summary, from, to }: Props) {
  const cells = [
    { label: '활성 일수', value: `${summary.activeDays}일` },
    { label: '총 문제 수', value: `${summary.totalTestCount}` },
    { label: '총 사용시간', value: formatDuration(summary.totalUseTime) },
    {
      label: '평균 점수 (가중)',
      value: summary.avgScoreOverall === null ? '—' : `${summary.avgScoreOverall}점`,
    },
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {title} ({from} ~ {to})
        </Typography>
        <Grid container spacing={2}>
          {cells.map((c) => (
            <Grid size={{ xs: 6, sm: 3 }} key={c.label}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {c.label}
                </Typography>
                <Typography variant="h6">{c.value}</Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          콘텐츠별 평균 (testCount 가중)
        </Typography>
        <Grid container spacing={1}>
          {CONTENT_ORDER.map((t) => {
            const v = summary.byContent[t];
            return (
              <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={t}>
                <Stack
                  spacing={0.25}
                  sx={{
                    p: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {CONTENT_LABELS[t]}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {v === undefined ? '—' : `${v}점`}
                  </Typography>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
}
